import { useEffect, useRef, useState } from 'react';
import type { OnboardingStateAnswer } from '../../../../../music24-api/src/trpc/onboarding.router';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { useTourActive } from '../../../redux/slices/appInfo/hooks';
import { takeSignupLandingRoute } from '../signupLanding';

/**
 * `/app/welcome` shows one time. A person who already saw it (a typed address, an old tab, the
 * Back button) goes to Home. The first answer of this page view decides, so the buttons of the
 * page, which set `showWelcome: false` themselves and then open their own page, are not changed.
 *
 * Two cases keep the page:
 * - the tour runs: "Continue to demo" leaves through the tour;
 * - an owner with no artist: "Add my song" sends that owner here to add the artist first.
 *
 * Returns true while the redirect runs; the page then shows the loading view, not the form.
 */
export function useSeenWelcomeRedirect(state: OnboardingStateAnswer | undefined): boolean {
  const { replace } = useSMLRouter();
  const isTourActive = useTourActive();
  const decidedRef = useRef(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (state === undefined || decidedRef.current) {
      return;
    }
    decidedRef.current = true;
    const needsArtist = !state.isMember && state.artist === null;
    if (state.showWelcome || isTourActive || needsArtist) {
      return;
    }
    setIsLeaving(true);
    replace(takeSignupLandingRoute() ?? SMLRoutes.home);
  }, [isTourActive, replace, state]);

  return isLeaving;
}
